import type { GroupingId } from './measures.js';

/* ===========================================================================
 * THE REPORTS THAT BECAME QUESTIONS.
 *
 * Seven fixed report pages read exactly the endpoints seven groups of measures
 * read. They were not similar to a builder question — they WERE one, written
 * out by hand, with the measure, the cut and the columns decided in advance and
 * no way for a reader to change any of them.
 *
 * So each one is retired into the sentence that reproduces it. That is only
 * honest because of where the figures come from: the builder aggregates the
 * SAME endpoint the page called, through the same server-side
 * `resolveReportScope`, so the numbers a reader saw yesterday on
 * `/reports/worksite-hours` are the numbers they see today on the composed
 * question. A retirement that changed the answer would be worse than keeping
 * the page.
 *
 * ---------------------------------------------------------------------------
 * WHY THE ROUTES SURVIVE AS REDIRECTS.
 *
 * These paths are in bookmarks, in emailed links, in support tickets and in
 * whatever a site manager pinned to their browser toolbar eighteen months ago.
 * A 404 tells that reader the product lost their report; a redirect that
 * carries their `?from=&to=&worksiteId=` across tells them it moved, and lands
 * them on the same figures they were looking at.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS NOT HERE, AND WHY IT NEVER WILL BE.
 *
 * The rest of the catalog is not a set of aggregation questions and cannot
 * become one:
 *
 *   • WPS SIF and the ERP CLOCKCARDSBATCH are FILE LAYOUTS a bank and an ERP
 *     parse. The output is a format, not a table.
 *   • Labour compliance and camp over-capacity EVALUATE A RULE. The output is a
 *     breach, not a sum.
 *   • Document expiry and the expiry board are indexed by a FUTURE DATE. The
 *     builder's axis is a period that has already happened.
 *   • Gratuity and leave balance are PER-WORKER ARITHMETIC over inputs.
 *   • Face coverage is a GAP LIST — workers with no template. The builder can
 *     only count what was recorded.
 *   • The monthly timesheet and the executive overview are DOCUMENT LAYOUTS: a
 *     calendar grid of punch pairs, and many KPIs at once.
 *
 * Forcing any of those into "one measure over a period, grouped by a dimension"
 * would mean inventing a figure to fill the shape. They stay as they are.
 * ======================================================================== */

export interface RetiredReport {
  /** The path that used to serve this report, still routed as a redirect. */
  from: string;
  /** Catalog id, so the row keeps its translation through the move. */
  id: string;
  /** The measure the sentence names — the one the page led with. */
  measure: string;
  /** The other figures the page showed, as columns. */
  plus?: readonly string[];
  grouping: GroupingId;
}

/**
 * Every retirement, and the question that replaces it.
 *
 * `retiredReports.test.ts` resolves each of these through `resolveQuestion` and
 * fails if any composes a REFUSAL — which is the failure this table invites,
 * because a measure's `groupableBy` or a companion's source can change without
 * anyone thinking to re-read this file. A retired report that redirects to a
 * refusal is worse than a 404: it tells the reader their report exists and then
 * declines to draw it.
 */
export const RETIRED_REPORTS: readonly RetiredReport[] = [
  {
    // "Active workers by department and employment status." Two columns and a
    // count — a grouping and a figure with the dropdowns painted on.
    from: '/reports/headcount',
    id: 'headcount',
    measure: 'activeHeadcount',
    grouping: 'department',
  },
  {
    // "Active workers with no enrolled face template, per site." The page drew
    // assigned / enrolled / unenrolled / coverage as four columns; that is one
    // measure and three companions.
    from: '/reports/coverage',
    id: 'coverage',
    measure: 'unenrolledWorkers',
    plus: ['assignedWorkers', 'enrolledWorkers', 'faceCoverageRate'],
    grouping: 'site',
  },
  {
    // "Resident allocation vs bed capacity per camp, flagging camps over
    // capacity." Residents, beds and the fill between them.
    from: '/reports/camp-capacity',
    id: 'campCapacity',
    measure: 'campResidents',
    plus: ['campBeds', 'campFill'],
    grouping: 'camp',
  },
  {
    // "Headcount, worked hours and OT per company branch (legal entity)."
    // `company` was already a declared cut on all three figures — this page was
    // a question nobody had composed.
    from: '/reports/legal-entities',
    id: 'legalEntities',
    measure: 'peopleWithRecord',
    plus: ['workedHours', 'overtimeHours'],
    grouping: 'company',
  },
  {
    // "Consecutive-day and weekly-hour limit breaches." Counted per breach,
    // cut by person — the page's own row.
    from: '/reports/compliance',
    id: 'compliance',
    measure: 'limitBreaches',
    grouping: 'person',
  },
  {
    /* "Expiries bucketed expired / 30 / 60 / 90 days for compliance
       follow-up." The board WAS a grouping: four columns, one per bucket, of
       the same count. Cut by `expiryBucket` it is that board, in the one screen
       that can also cut it by document type or by person without a second
       page being written.

       The flat `/reports/document-expiry` list retires onto the same question:
       it showed the same rows unbucketed, which is this cut with the grouping
       changed rather than a different report. */
    from: '/reports/document-expiry-buckets',
    id: 'documentExpiryBuckets',
    measure: 'expiringDocuments',
    grouping: 'expiryBucket',
  },
  {
    from: '/reports/document-expiry',
    id: 'documentExpiry',
    measure: 'expiringDocuments',
    grouping: 'documentType',
  },
  {
    // "Total hours per project over the period — normal, OT1 and rest-day OT2."
    // Three columns is exactly what the page showed, and the reason companion
    // columns had to exist before any of this could be retired.
    from: '/reports/worksite-hours',
    id: 'worksiteHours',
    measure: 'normalHours',
    plus: ['ot1Hours', 'ot2Hours'],
    grouping: 'site',
  },
  {
    // "Headcount, attendance % and OT per site over a date range."
    from: '/reports/manpower-trend',
    id: 'manpowerTrend',
    measure: 'assignedHeadcount',
    plus: ['attendanceRate', 'siteOvertime'],
    grouping: 'site',
  },
  {
    // "Billable hours and headcount per labour supplier, for invoice
    // reconciliation." One figure, one cut — the page was a builder question
    // with the dropdowns painted on.
    from: '/reports/suppliers',
    id: 'suppliers',
    measure: 'supplierBilled',
    grouping: 'supplier',
  },
  {
    // "Every day a worker arrived past their grace window, with the minutes
    // late." Two figures: how often, and how much time.
    from: '/reports/late',
    id: 'late',
    measure: 'lateArrivalDays',
    plus: ['lateMinutes'],
    grouping: 'person',
  },
  {
    // "Every day with overtime beyond the scheduled shift, with the minutes."
    from: '/reports/overtime',
    id: 'overtime',
    measure: 'overtimeDaysCount',
    plus: ['overtimeDayHours'],
    grouping: 'person',
  },
  {
    // "Days marked incomplete — checked in but never out."
    from: '/reports/missed-punches',
    id: 'missedPunches',
    measure: 'missedPunchDays',
    grouping: 'person',
  },
  {
    /* "Per-site time per worker per day — first in, last out, worked and OT."
       THE ONE PARTIAL RETIREMENT, and it is named rather than glossed: no
       measure exposes a punch PAIR, so first-in / last-out do not survive the
       move. What does survive is the figure that page was actually read for —
       the gap between sites — and the day-level punch detail remains reachable
       from the attendance day views. If punch pairs ever become a measure this
       row gains them as columns; until then this is a smaller report than the
       page was, and saying so is the point of this comment. */
    from: '/reports/worker-site',
    id: 'workerSite',
    measure: 'betweenSiteGap',
    grouping: 'person',
  },
];

/**
 * The builder URL for a retirement, as a search string WITHOUT the scope.
 *
 * The scope half is deliberately absent: the redirect carries the reader's own
 * `from`/`to`/`worksiteId` across verbatim, and the catalog link carries
 * whatever scope the reader last used. Baking a period in here would mean a
 * catalog row that silently resets the range a reader had already chosen.
 */
export function builderQuestionSearch(report: RetiredReport): string {
  const params = new URLSearchParams();
  params.set('measure', report.measure);
  if (report.plus !== undefined && report.plus.length > 0) {
    params.set('plus', report.plus.join(','));
  }
  params.set('groupBy', report.grouping);
  return params.toString();
}

/** The full path a catalog row points at. */
export function builderQuestionPath(report: RetiredReport): string {
  return `/reports/builder?${builderQuestionSearch(report)}`;
}
